import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormButtons from 'components/FormFields/FormButtons';
import FormTextField from 'components/FormFields/FormTextField';
import { useCustomForm } from 'lib/form/useCustomForm';
import { OrganizationCreateInput, OrganizationUpdateInput, useHasOrganizationsQuery } from 'lib/graphql/graphql';
import UnsavedFormQuitConfirmation from 'lib/routing/UnsavedFormQuitConfirmation';
import FormCurrencyField from 'modules/Organizations/components/FormCurrencyField';
import useOrganization from 'providers/organization/useOrganization';
import { useCallback } from 'react';
import { FormProvider, SubmitHandler } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { FormContainer, NeutralOutlinedButton, Title } from 'styles/styles';
import { useLocation } from 'wouter';

interface OrganizationFormProps {
  children?: React.ReactNode;
  defaultValues: OrganizationCreateInput | OrganizationUpdateInput;
  title: String;
  onSubmit: SubmitHandler<OrganizationCreateInput>;
  updating?: boolean
}

function OrganizationForm(props: OrganizationFormProps) {
  const {
    children,
    defaultValues,
    title,
    onSubmit,
    updating = false,
  } = props;

  const [, setLocation] = useLocation();
  const { setOrganizationId, setInvitationId } = useOrganization();

  const { data } = useHasOrganizationsQuery();
  const hasOrganizations = Boolean(data?.hasOrganizations);

  const formControls = useCustomForm({ defaultValues, mode: 'onChange', trim: true });

  const goToOrganizations = useCallback(() => {
    setOrganizationId(null);
    setInvitationId(null);
    setLocation('~/');
  }, [setInvitationId, setLocation, setOrganizationId]);

  return (
    <FormProvider {...formControls}>
      <UnsavedFormQuitConfirmation />
      <FormContainer>
        {hasOrganizations && (
          <div>
            <NeutralOutlinedButton
              startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
              onClick={goToOrganizations}
            >
              <FormattedMessage id="return_to_organizations" />
            </NeutralOutlinedButton>
          </div>
        )}
        <Title>
          <FormattedMessage id={`organization_${title}`} />
        </Title>
        <FormTextField
          required
          name="name"
          labelId="organization_name"
          fieldDescription="organization_form_name"
          rules={{ maxLength: 50 }}
        />
        <FormTextField
          rows={3}
          multiline
          name="description"
          labelId="description"
          fieldDescription="organization_form_description"
          rules={{ maxLength: 300 }}
        />
        <FormCurrencyField
          disabled={updating}
          name="currency"
          labelId="organization_form_currency"
          fieldDescription="organization_form_currency_description"
        />
      </FormContainer>
      {children}
      <FormButtons submit={onSubmit} />
    </FormProvider>
  );
}

export default OrganizationForm;
